/*
 * Cart drawer: keep the purchase on top and hang its free gifts underneath.
 *
 * Shopify returns cart lines in its own order, which put the gifts above the
 * product that earned them — so the first thing a customer saw was a $0 line
 * with no explanation. This reorders the rendered rows and marks the gift ones
 * so they read as belonging to the purchase rather than as separate buys.
 *
 * A gift is identified by its line total being zero rather than by a hardcoded
 * handle, so adding or renaming gift products needs no change here.
 *
 * Horizon re-renders the drawer wholesale on every cart change, so nothing is
 * cached: the work is redone on the cart event and re-checked briefly after,
 * since the new markup lands a frame or two later.
 */
(() => {
  const ROW = 'tr.cart-items__table-row';
  const GIFT = 'nv-cart-gift';
  const FIRST = 'nv-cart-gift--first';

  const ZERO = /(^|\s)([^\d\s]{0,3})0[.,]00/;

  const isGift = (row) => {
    // Once relabelled the price no longer reads as zero, so the row remembers.
    if (row.dataset.nvGift === '1') return true;
    const price = row.querySelector('.cart-items__price, [class*="price"]');
    const text = (price || row).textContent || '';
    return ZERO.test(text.replace(/\s+/g, ' '));
  };

  // "Free" says what a zero costs; "$0.00" makes the reader do the work.
  const labelFree = (row) => {
    row.querySelectorAll('.cart-items__price *').forEach((node) => {
      if (node.children.length) return;
      const text = (node.textContent || '').trim();
      if (ZERO.test(' ' + text)) {
        node.dataset.nvWas = text;
        node.textContent = window.NovelleFreeLabel || 'Free';
      }
    });
  };

  /*
   * Shipping protection is a service, not something bought — it belongs under
   * the order, not above it. Matched on the product link rather than the
   * title, so renaming it changes nothing here. It has no image, which is why
   * the row filter below would otherwise skip it entirely and leave it sitting
   * at the top of the cart looking like the main purchase.
   */
  const PROTECTION = 'nv-cart-protection';
  const isProtection = (row) =>
    !!row.querySelector('a[href*="/products/shipping-protection"]');

  const arrange = () => {
    document.querySelectorAll('.cart-items__table').forEach((table) => {
      const all = [...table.querySelectorAll(ROW)];
      const protection = all.filter(isProtection);
      protection.forEach((r) => r.classList.add(PROTECTION));

      const rows = all.filter((r) => r.querySelector('img') && !isProtection(r));
      if (!rows.length) {
        // Nothing to sort around, but protection still goes last.
        protection.forEach((r) => table.querySelector('tbody')?.appendChild(r));
        return;
      }
      if (rows.length < 2 && !protection.length) return;

      const gifts = [];
      const bought = [];
      rows.forEach((row) => {
        row.classList.remove(GIFT, FIRST);
        if (isGift(row)) {
          row.dataset.nvGift = '1';
          labelFree(row);
          gifts.push(row);
        } else {
          bought.push(row);
        }
      });

      if (gifts.length && bought.length) {
        // Insert after the last purchased line. Reversed, because each after()
        // lands immediately behind the anchor and would otherwise flip the order.
        const anchor = bought[bought.length - 1];
        [...gifts].reverse().forEach((g) => anchor.after(g));

        gifts.forEach((g) => g.classList.add(GIFT));
        gifts[0].classList.add(FIRST);
      }

      // Last, under both the purchase and its gifts.
      const body = table.querySelector('tbody') || table;
      protection.forEach((r) => body.appendChild(r));
    });
  };

  // The drawer swaps its markup after the event fires, so re-run for a moment.
  const arrangeSoon = () => {
    arrange();
    let n = 0;
    const t = setInterval(() => {
      arrange();
      if (++n > 8) clearInterval(t);
    }, 150);
  };

  document.addEventListener('shopify:cart:lines-update', arrangeSoon);
  document.addEventListener('DOMContentLoaded', arrangeSoon);
  arrangeSoon();
})();

/*
 * Cart hold countdown.
 *
 * Lives here rather than inline in snippets/novelle-cart-timer.liquid: Horizon
 * re-renders the drawer by injecting markup, and a <script> arriving that way
 * never executes. When a visitor's drawer was first rendered empty, the inline
 * version simply never started and the clock stayed on --:-- for the session.
 *
 * The deadline is per-visitor in localStorage — a cart hold has no shared
 * deadline to derive. Every tick re-queries the DOM, because a node captured
 * once is detached by the next re-render.
 */
(() => {
  const KEY = 'nv-cart-deadline';
  if (window.__nvCartTimer) return;
  window.__nvCartTimer = true;

  let expiring = false;

  const expire = async (mode) => {
    if (expiring) return;
    /*
     * Never in the theme editor. This clears the cart and reloads, and a
     * reload inside the editor's canvas drops the session — the editor
     * reports the page as failing to load rather than as a timer firing.
     */
    if (window.Shopify && window.Shopify.designMode) return;
    expiring = true;
    localStorage.removeItem(KEY);
    if (mode === 'clear') {
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (e) {
        /* offline — the reload below still reflects real cart state */
      }
      window.location.reload();
    } else {
      document.querySelectorAll('[data-nv-cart-timer]').forEach((b) => (b.hidden = true));
      expiring = false;
    }
  };

  const pad = (n) => String(n).padStart(2, '0');

  const tick = () => {
    const bars = document.querySelectorAll('[data-nv-cart-timer]');
    if (!bars.length) return;

    const bar = bars[0];
    const minutes = Number(bar.dataset.minutes || 5);

    /*
     * data-count is server-rendered and goes stale the moment a line is added
     * without a full page load, so the live cart element count wins and
     * data-count is only the fallback before anything has rendered.
     */
    const rows = document.querySelectorAll('tr.cart-items__table-row img').length;
    const count = rows || Number(bar.dataset.count || 0);
    const empty = count < 1 || Boolean(document.querySelector('.cart-drawer--empty'));

    if (empty) {
      localStorage.removeItem(KEY);
      bars.forEach((b) => (b.hidden = true));
      return;
    }

    let deadline = Number(localStorage.getItem(KEY));
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + minutes * 60000;
      localStorage.setItem(KEY, String(deadline));
    }

    const left = deadline - Date.now();
    if (left <= 0) return expire(bar.dataset.expire);

    const label = `${pad(Math.floor(left / 60000))}:${pad(Math.floor((left % 60000) / 1000))}`;
    bars.forEach((b) => {
      b.hidden = false;
      const clock = b.querySelector('[data-nv-ct-clock]');
      if (clock) clock.textContent = label;
    });
  };

  tick();
  setInterval(tick, 1000);
})();

/*
 * "You save" in the cart drawer.
 *
 * /cart.js knows what a line costs and what it was discounted from, but not
 * its compare-at price — so a bundle priced at $49 against a $87 compare-at
 * shows no saving at all from cart data alone. The compare-at lives on the
 * product JSON, which is fetched once per product and cached for the session.
 *
 * Two kinds of saving are added together:
 *   - compare-at vs price      (the bundle discount baked into the variant)
 *   - original vs final line   (automatic discounts, ie. the free gifts)
 */
(() => {
  const compareCache = new Map();
  let busy = false;

  /*
   * en-US, not the visitor's locale. The theme renders "$49.00" from the
   * shop's money format, and a browser set to German turned the struck-out
   * figure beside it into "87,00 $" — two number formats in one row.
   */
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const compareMap = async (handle) => {
    if (compareCache.has(handle)) return compareCache.get(handle);
    const map = new Map();
    try {
      const p = await fetch(`/products/${handle}.js`).then((r) => r.json());
      (p.variants || []).forEach((v) => map.set(Number(v.id), Number(v.compare_at_price || 0)));
    } catch (e) {
      // Leave it empty: a product we cannot read contributes no saving rather
      // than blocking the whole row.
    }
    compareCache.set(handle, map);
    return map;
  };

  const clear = () => {
    document.querySelectorAll('.nv-save__was, .nv-save__line').forEach((n) => n.remove());
    document.querySelectorAll('.cart-totals__original-container').forEach((s) => {
      s.innerHTML = '';
      s.hidden = true;
    });
  };

  const render = async () => {
    if (busy) return;
    busy = true;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const rows = [...document.querySelectorAll('.cart-totals__total')];
      if (!rows.length) return;

      if (!cart.items.length) return clear();

      const handles = [...new Set(cart.items.map((i) => i.handle).filter(Boolean))];
      const maps = new Map(await Promise.all(handles.map(async (h) => [h, await compareMap(h)])));

      let was = 0;
      cart.items.forEach((item) => {
        const compare = (maps.get(item.handle) || new Map()).get(Number(item.variant_id)) || 0;
        // Whichever is higher: the crossed-out price should never be lower
        // than what is actually being charged.
        const unit = Math.max(compare, item.original_price || item.price);
        was += unit * item.quantity;
      });

      const now = cart.total_price;
      const saving = was - now;

      clear();
      if (saving <= 0) return;

      /*
       * Written into the Estimated total row itself rather than into
       * Horizon's original-container above it, so the old price sits directly
       * beside the new one — the comparison only works when the eye can make
       * it without moving.
       */
      rows.forEach((row) => {
        const label = row.querySelector('.cart-totals__total-label');
        const value = row.querySelector('.cart-totals__total-value');
        if (!label || !value) return;

        /*
         * The pill is wrapped rather than dropped straight in. On a phone
         * there is 55px left on the line and the pill needs 84 even shortened,
         * so it cannot share the row — the wrapper is display:contents on
         * desktop, where it changes nothing, and a full-width flex line on
         * mobile, which puts the pill on its own row without the rose
         * background stretching the full width.
         */
        const line = document.createElement('span');
        line.className = 'nv-save__line';
        const badge = document.createElement('span');
        badge.className = 'nv-save__badge';
        badge.textContent = 'You save ' + money(saving, cart.currency);
        line.appendChild(badge);
        label.insertAdjacentElement('afterend', line);

        const oldPrice = document.createElement('s');
        oldPrice.className = 'nv-save__was';
        oldPrice.textContent = money(was, cart.currency);
        value.insertAdjacentElement('beforebegin', oldPrice);
      });
    } catch (e) {
      // A failed lookup must never take the totals row down with it.
    } finally {
      busy = false;
    }
  };

  /*
   * Horizon replaces the drawer wholesale, so the row has to be written again
   * on every render rather than once. Same reasoning as the gift ordering
   * above: watch, do not cache.
   */
  const watch = () => {
    const host = document.querySelector('cart-drawer-component');
    if (!host || host.dataset.nvSaveWatched) return;
    host.dataset.nvSaveWatched = '1';
    new MutationObserver(() => {
      if (!document.querySelector('.nv-save__was')) render();
    }).observe(host, { childList: true, subtree: true });
  };

  document.addEventListener('shopify:cart:lines-update', () => { render(); watch(); });
  document.addEventListener('DOMContentLoaded', () => { render(); watch(); });
  render();
  watch();
})();
